import { PhoneInputValue } from "../PhoneInput.types";
import { useCallback } from "react";
import { isValidPhoneNumber, formatIncompletePhoneNumber, parseIncompletePhoneNumber } from "libphonenumber-js";
import { useControlled } from "@commonlibs";
import useTranslate from "ui/locale/hooks/useTranslate";

export interface UsePhoneInputParams {
    required?: boolean;
    defaultValue: PhoneInputValue;
    value?: PhoneInputValue;
    onChange?: (value: PhoneInputValue) => void;
}

export interface PhoneInputFormControlComps {
    handlePhoneCountryCodeChange: (phoneCountryCode: string | undefined) => void;
    handlePhoneNumberChange: (phoneNumber: string) => void;
    format: (value: string) => string;
    parse: (value: string) => string;
}

export interface PhoneInputComps {
    codeInput: PhoneInputValue;
    setCodeInput: (value: PhoneInputValue) => void;
    hideError: boolean;
    validation: ([code, number]: PhoneInputValue) => [boolean, string | undefined];
    handlePhoneInputFormControl: (value: PhoneInputValue, setValue: (value: PhoneInputValue) => void) => PhoneInputFormControlComps;
}

export default function usePhoneInput({ required = false, defaultValue, value, onChange }: UsePhoneInputParams): PhoneInputComps {
    const translateError = useTranslate("error");

    const [codeInput, setCodeInput] = useControlled(defaultValue, value, onChange);

    const handlePhoneInputFormControl = (
        value: PhoneInputValue,
        setValue: (value: PhoneInputValue) => void,
    ): PhoneInputFormControlComps => {
        const handlePhoneCountryCodeChange = (phoneCountryCode: string | undefined) => {
            setValue([phoneCountryCode, value[1]]);
        };

        const handlePhoneNumberChange = (phoneNumber: string) => {
            setValue([value[0], phoneNumber]);
        };

        const format = (val: string) => {
            if (!value[0]) return val;
            return formatIncompletePhoneNumber(`+${value[0]}${val}`).replace(new RegExp(`\\+${value[0]} ?`), "");
        };
        const parse = (val: string) => {
            if (!value[0]) return val;
            return parseIncompletePhoneNumber(`+${value[0]}${val}`).replace(new RegExp(`\\+${value[0]} ?`), "");
        };

        return {
            handlePhoneCountryCodeChange,
            handlePhoneNumberChange,
            format,
            parse,
        };
    };

    const validation = useCallback(
        ([code, number]: PhoneInputValue): [boolean, string | undefined] => {
            if ((!code && !number) || code === "" || number === "") return [!required, translateError("invalid_required")];
            else if (code && number && isValidPhoneNumber(`+${code}${number}`)) return [true, undefined];
            else return [false, translateError("invalidPhoneNumber")];
        },
        [required],
    );

    return {
        validation,
        handlePhoneInputFormControl,
        codeInput,
        setCodeInput,
        hideError: required && codeInput[1] === "",
    };
}
